import React, { useState, useRef } from "react";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  Crop,
  PixelCrop,
} from "react-image-crop";
import { FileModel } from "@/types/common";
import "react-image-crop/dist/ReactCrop.css";
import { Button } from "../Button";
import { Image } from "./Image";

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export const ImageCrop = ({
  imgSrc,
  onImageCropped,
}: {
  imgSrc?: string;
  croppedImgSrc?: string;
  onImageCropped: (img: FileModel) => void;
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [croppedImg, setCroppedImg] = useState<FileModel>();

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 1));
  }

  async function onCropComplete(completedCrop: PixelCrop) {
    const image = imgRef.current;
    if (!image || !completedCrop) {
      throw new Error("Crop canvas does not exist");
    }
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const offscreen = new OffscreenCanvas(
      completedCrop.width,
      completedCrop.height
    );
    const ctx = offscreen.getContext("2d");
    if (!ctx) {
      throw new Error("No 2d context");
    }

    ctx.drawImage(
      image,
      completedCrop.x,
      completedCrop.y,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );
    const blob = await offscreen.convertToBlob({
      type: "image/png",
    });

    const fileReader = new FileReader();
    fileReader.readAsDataURL(blob);
    fileReader.onloadend = function () {
      const fileUrl = fileReader.result?.toString().split(",");
      if (fileUrl && fileUrl.length > 1) {
        setCroppedImg({
          fileDataAsBase64: fileUrl[1],
          fileName: "",
        });
      }
    };
  }

  const handleSave = () => {
    if (croppedImg)
      onImageCropped({
        ...croppedImg,
        fileName: `cropped_${Date.parse(new Date().toString())}.jpg`,
      });

    setCroppedImg(undefined);
  };

  return (
    <div className="flex justify-start">
      {!!imgSrc && (
        <ReactCrop
          crop={crop}
          onChange={setCrop}
          onComplete={onCropComplete}
          className="h-20 w-20 m-2"
        >
          <img
            ref={imgRef}
            alt="Crop me"
            src={imgSrc}
            onLoad={onImageLoad}
            className="h-20 w-20"
          />
        </ReactCrop>
      )}
      {!!croppedImg?.fileDataAsBase64 && (
        <>
          <div className="h-20 w-20 m-2">
            <Image
              fileDataAsBase64={croppedImg.fileDataAsBase64}
              className="h-full w-full"
              alt="Cropped Img"
            />
          </div>
          <Button size="sm" onClick={handleSave}>
            Save
          </Button>
        </>
      )}
    </div>
  );
};
